import axios, { AxiosInstance } from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'; 

export const docMgmtInstance : AxiosInstance = axios.create({
  baseURL: "https://data-core.teampbs.com/DocMgmt/api",
  timeout: 120000,
});

// Helper function to check if error requires interaction
const isInteractionRequiredError = (error: any): boolean => {
  return error instanceof InteractionRequiredAuthError ||
         error.errorCode === "interaction_required" ||
         error.errorCode === "consent_required" ||
         error.errorCode === "login_required" ||
         error.errorCode === "token_expired";
};

export const initializeDocMgmtService = (msalApp: PublicClientApplication) => {
  docMgmtInstance.interceptors.request.use(async function (config) {
    if (!msalApp) throw new Error("MSAL not initialized");
    
    const activeAccount = msalApp.getActiveAccount();
    const accounts = msalApp.getAllAccounts();
    
    // Handle unauthenticated state
    if (!activeAccount && accounts.length === 0) {
      console.warn("No active MSAL account found for DocMgmt service. User is not authenticated.");
      
      // Check if authentication is already in progress
      try {
        const activeRequest = (msalApp as any).browserStorage?.getInteractionInProgress();
        if (activeRequest) {
          console.info("Authentication already in progress, skipping DocMgmt API call");
          return Promise.reject(new Error("Authentication in progress - DocMgmt API call will retry after login"));
        }
      } catch (checkError) {
        // Ignore errors checking interaction state
      }
      
      // Don't initiate auth flow here - let the app-level authentication handle it
      console.info("Deferring DocMgmt authentication to app-level flow");
      return Promise.reject(new Error("Authentication required - handled by app level"));
    }
    
    const tokenRequest = {
      scopes: ["api://0d377300-7250-4d26-8085-4b64ac649916/access_as_user"],
      account: activeAccount || accounts[0],
    };
    
    try {
      const tokenResponse = await msalApp.acquireTokenSilent(tokenRequest);
      config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return config;
    } catch (error: any) {
      console.error("Error acquiring token silently for DocMgmt service:", error);
      
      // Handle specific MSAL errors that require interaction
      if (isInteractionRequiredError(error)) {
        console.info("Interactive authentication required for DocMgmt. Checking for ongoing interactions...");
        
        // Check if interaction is already in progress
        try {
          const activeRequest = (msalApp as any).browserStorage?.getInteractionInProgress();
          if (activeRequest) {
            console.info("Authentication interaction already in progress, skipping DocMgmt token request");
            return Promise.reject(new Error("Authentication in progress - DocMgmt token will be acquired after login"));
          }
        } catch (checkError) {
          // Ignore errors checking interaction state
        }
        
        // Don't initiate auth flow here - let the app-level authentication handle it
        console.info("Deferring DocMgmt interactive authentication to app-level flow");
        return Promise.reject(new Error("Interactive authentication required - handled by app level"));
      }
      
      return Promise.reject(error);
    }
  });
  
  // Add response interceptor for 401 handling
  docMgmtInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        console.warn("Received 401 Unauthorized response from DocMgmt service. Deferring to app-level authentication.");
        // Don't initiate login here - let the app handle it
      }
      return Promise.reject(error);
    }
  );
};